import React from "react";
import BrowserFrame from "./BrowserFrame";

// Micro-UI pieces used inside every mock (dark, aggressive, "command center")
const Chip = ({ children, tone = "accent" }) => {
  const toneStyles = {
    accent: { color: "var(--mk-accent)", borderColor: "var(--mk-border-accent)" },
    primary: { color: "#60a5fa", borderColor: "rgba(96,165,250,0.35)" },
    success: { color: "#10b981", borderColor: "rgba(16,185,129,0.35)" },
    warn: { color: "#f59e0b", borderColor: "rgba(245,158,11,0.35)" },
    danger: { color: "#ef4444", borderColor: "rgba(239,68,68,0.35)" },
    muted: { color: "var(--mk-text-dim)", borderColor: "var(--mk-border-subtle)" },
  };
  return (
    <span
      className="mk-mono text-[9px] font-bold uppercase tracking-[0.2em] px-2 py-0.5 rounded-full border"
      style={{
        background: "rgba(255,255,255,0.03)",
        ...(toneStyles[tone] || toneStyles.accent),
      }}
    >
      {children}
    </span>
  );
};

const Row = ({ children, last }) => (
  <div
    className="flex items-center justify-between px-4 py-3"
    style={{ borderBottom: last ? "none" : "1px solid var(--mk-border-subtle)" }}
  >
    {children}
  </div>
);

const Sidebar = () => (
  <div
    className="hidden md:flex flex-col gap-1 py-4 px-3 border-r"
    style={{ borderColor: "var(--mk-border-subtle)", background: "rgba(255,255,255,0.015)", minWidth: 170 }}
  >
    {["Dashboard", "Jobs", "Quotes", "Invoices", "Suppliers", "Assets", "Reports"].map((t, i) => (
      <div
        key={t}
        className="px-3 py-1.5 rounded text-xs"
        style={{
          background: i === 1 ? "rgba(19,93,212,0.18)" : "transparent",
          color: i === 1 ? "var(--mk-accent)" : "var(--mk-text-dim)",
          fontWeight: i === 1 ? 700 : 500,
        }}
      >
        {t}
      </div>
    ))}
  </div>
);

const Bar = ({ w, color }) => (
  <div style={{ height: 8, borderRadius: 2, background: color || "var(--mk-border-subtle)", width: `${w}%` }} />
);

// ──────────────────────────────────────────────────────────────────────────
// JOBS — detail
export const JobsDetailMock = () => (
  <BrowserFrame url="app.fixlr.app/jobs/J-2041">
    <div className="flex min-h-[340px]">
      <Sidebar />
      <div className="flex-1 p-5 space-y-4">
        <div className="flex items-center justify-between">
          <div>
            <div className="text-xs text-[var(--mk-text-muted)] mk-mono">JOB / J-2041</div>
            <div className="text-lg font-bold">Boiler service — 14 Mare St, E8</div>
          </div>
          <div className="flex items-center gap-2">
            <Chip tone="success">Scheduled</Chip>
            <Chip tone="warn">Priority: High</Chip>
          </div>
        </div>
        <div className="grid grid-cols-3 gap-3">
          {[
            { t: "Schedule", v: "Tue 14:30 — 90 min", sub: "Reminder sent 06:30" },
            { t: "Supplier", v: "North Heat Ltd", sub: "★ 4.8 · SLA 96%" },
            { t: "In-House Operative", v: "Jay Patel", sub: "🟢 On shift · 4km away" },
          ].map((c) => (
            <div key={c.t} className="rounded-lg p-3 border" style={{ borderColor: "var(--mk-border-subtle)", background: "rgba(255,255,255,0.02)" }}>
              <div className="mk-mono text-[9px] uppercase tracking-[0.2em] text-[var(--mk-text-muted)] mb-1">{c.t}</div>
              <div className="text-sm font-semibold">{c.v}</div>
              <div className="text-xs text-[var(--mk-text-dim)]">{c.sub}</div>
            </div>
          ))}
        </div>
        <div className="rounded-lg border overflow-hidden" style={{ borderColor: "var(--mk-border-subtle)" }}>
          <div className="px-4 py-2 text-xs mk-mono uppercase tracking-[0.2em] text-[var(--mk-text-muted)]" style={{ background: "rgba(255,255,255,0.02)" }}>
            Activity
          </div>
          <Row>
            <span className="text-xs">✅ Quote accepted by client</span>
            <span className="text-xs text-[var(--mk-text-muted)]">Mon 09:12</span>
          </Row>
          <Row>
            <span className="text-xs">📎 Gas safety sheet auto-attached</span>
            <span className="text-xs text-[var(--mk-text-muted)]">Mon 09:13</span>
          </Row>
          <Row last>
            <span className="text-xs">📲 Push sent to Jay Patel</span>
            <span className="text-xs text-[var(--mk-text-muted)]">Mon 09:14</span>
          </Row>
        </div>
      </div>
    </div>
  </BrowserFrame>
);

// JOBS — assign picker
export const JobsAssignMock = () => (
  <BrowserFrame url="app.fixlr.app/jobs/new">
    <div className="p-5 min-h-[320px] space-y-4">
      <div className="text-sm font-semibold">New job — assignment</div>
      <div className="grid grid-cols-2 gap-4">
        <div className="rounded-lg p-3 border" style={{ borderColor: "var(--mk-border-subtle)" }}>
          <div className="mk-mono text-[10px] uppercase tracking-[0.2em] text-[var(--mk-accent)] mb-2">External Supplier</div>
          {["North Heat Ltd — ★4.8", "Capital Plumbing — ★4.6", "Greenline Gas — ★4.9"].map((t, i) => (
            <div
              key={t}
              className="text-xs px-2 py-2 rounded mb-1 flex items-center justify-between"
              style={{
                background: i === 0 ? "rgba(19,93,212,0.18)" : "transparent",
                color: i === 0 ? "var(--mk-text)" : "var(--mk-text-dim)",
                border: i === 0 ? "1px solid var(--mk-border-accent)" : "1px solid transparent",
              }}
            >
              <span>{t}</span>
              {i === 0 && <Chip tone="accent">Selected</Chip>}
            </div>
          ))}
        </div>
        <div className="rounded-lg p-3 border" style={{ borderColor: "var(--mk-border-subtle)" }}>
          <div className="mk-mono text-[10px] uppercase tracking-[0.2em] text-[var(--mk-accent)] mb-2">In-House Operative</div>
          {["Jay Patel — 🟢 On shift", "Nina Cole — 🟡 Break", "Priya Shah — 🔴 Off"].map((t, i) => (
            <div
              key={t}
              className="text-xs px-2 py-2 rounded mb-1"
              style={{
                background: i === 0 ? "rgba(214,239,55,0.1)" : "transparent",
                color: i === 0 ? "var(--mk-accent)" : "var(--mk-text-dim)",
                border: i === 0 ? "1px solid var(--mk-border-accent)" : "1px solid transparent",
              }}
            >
              {t}
            </div>
          ))}
        </div>
      </div>
      <div className="flex justify-end gap-2">
        <div className="mk-btn mk-btn-secondary text-xs">Save draft</div>
        <div className="mk-btn mk-btn-primary text-xs">Create & notify →</div>
      </div>
    </div>
  </BrowserFrame>
);

// JOBS — costings
export const JobsCostingsMock = () => (
  <BrowserFrame url="app.fixlr.app/invoices/new">
    <div className="p-5 min-h-[320px]">
      <div className="text-sm font-semibold mb-3">Line items</div>
      <div className="rounded-lg overflow-hidden border" style={{ borderColor: "var(--mk-border-subtle)" }}>
        <div className="grid grid-cols-12 px-3 py-2 text-[10px] mk-mono uppercase tracking-[0.2em] text-[var(--mk-text-muted)]" style={{ background: "rgba(255,255,255,0.02)" }}>
          <div className="col-span-6">Description</div>
          <div className="col-span-2 text-right">Qty</div>
          <div className="col-span-2 text-right">Unit £</div>
          <div className="col-span-2 text-right">Total</div>
        </div>
        {[
          ["Boiler service (annual)", 1, 120.0, 120.0],
          ["Replacement filter cartridge", 2, 24.5, 49.0],
          ["Emergency callout surcharge", 1, 45.0, 45.0],
        ].map(([d, q, u, t], i) => (
          <div key={i} className="grid grid-cols-12 px-3 py-2 text-xs border-t" style={{ borderColor: "var(--mk-border-subtle)" }}>
            <div className="col-span-6">{d}</div>
            <div className="col-span-2 text-right text-[var(--mk-text-dim)]">{q}</div>
            <div className="col-span-2 text-right text-[var(--mk-text-dim)]">£{u.toFixed(2)}</div>
            <div className="col-span-2 text-right font-semibold">£{t.toFixed(2)}</div>
          </div>
        ))}
      </div>
      <div className="grid grid-cols-2 mt-4 gap-4">
        <div />
        <div className="rounded-lg p-3 border text-xs space-y-1" style={{ borderColor: "var(--mk-border-accent)", background: "rgba(214,239,55,0.04)" }}>
          {[
            ["Cost", "£214.00"],
            ["+ Markup A (PM fee 15%)", "£32.10"],
            ["Subtotal", "£246.10"],
            ["+ Markup B (Client uplift 5%)", "£12.31"],
          ].map(([k, v]) => (
            <div key={k} className="flex justify-between text-[var(--mk-text-dim)]">
              <span>{k}</span><span>{v}</span>
            </div>
          ))}
          <div className="flex justify-between pt-1 border-t text-base font-bold" style={{ borderColor: "var(--mk-border-subtle)", color: "var(--mk-accent)" }}>
            <span>Total</span><span>£258.41</span>
          </div>
        </div>
      </div>
    </div>
  </BrowserFrame>
);

// EMERGENCY dashboard
export const EmergencyDashboardMock = () => (
  <BrowserFrame url="app.fixlr.app/emergency-jobs">
    <div className="p-5 min-h-[320px] space-y-3">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2">
          <span className="h-2.5 w-2.5 rounded-full bg-red-500 animate-pulse" />
          <div className="text-sm font-semibold">Active emergencies — 3</div>
        </div>
        <Chip tone="danger">RED ALERT</Chip>
      </div>
      {[
        { p: "92 Bethnal Green Rd", sla: "SLA 00:14 left", acc: "Jay Patel acknowledged" },
        { p: "Unit 4, Holloway Depot", sla: "SLA 00:48 left", acc: "Dispatched — en route" },
        { p: "Raven Court, E3", sla: "SLA 02:04 left", acc: "Awaiting acknowledgement" },
      ].map((e, i) => (
        <div
          key={i}
          className="rounded-lg p-3 border flex items-center justify-between"
          style={{
            borderColor: "rgba(239,68,68,0.35)",
            background: "rgba(239,68,68,0.05)",
          }}
        >
          <div>
            <div className="text-sm font-semibold">{e.p}</div>
            <div className="text-xs text-[var(--mk-text-dim)]">{e.acc}</div>
          </div>
          <div className="text-right">
            <div className="mk-mono text-xs" style={{ color: "#ef4444" }}>{e.sla}</div>
            <Chip tone="danger">P1</Chip>
          </div>
        </div>
      ))}
    </div>
  </BrowserFrame>
);

export const EmergencyEscalationMock = () => (
  <BrowserFrame url="app.fixlr.app/settings/escalation">
    <div className="p-5 min-h-[320px]">
      <div className="text-sm font-semibold mb-4">Escalation ladder</div>
      <div className="space-y-3">
        {[
          { t: "Tier 1 — First call", min: "0–10 min", n: "Jay Patel", c: "accent" },
          { t: "Tier 2 — Escalation", min: "10–25 min", n: "North Heat Ltd (on-call)", c: "warn" },
          { t: "Tier 3 — Hard escalation", min: "25+ min", n: "Duty Manager + SMS", c: "danger" },
        ].map((r) => (
          <div key={r.t} className="grid grid-cols-12 items-center gap-3">
            <div className="col-span-3 mk-mono text-[10px] uppercase tracking-[0.2em] text-[var(--mk-text-muted)]">{r.min}</div>
            <div className="col-span-9 rounded-lg p-3 border flex items-center justify-between" style={{ borderColor: "var(--mk-border-subtle)" }}>
              <div>
                <div className="text-sm font-semibold">{r.t}</div>
                <div className="text-xs text-[var(--mk-text-dim)]">{r.n}</div>
              </div>
              <Chip tone={r.c}>AUTO</Chip>
            </div>
          </div>
        ))}
      </div>
    </div>
  </BrowserFrame>
);

// QUOTES builder
export const QuotesBuilderMock = () => (
  <BrowserFrame url="app.fixlr.app/quotes/new">
    <div className="p-5 min-h-[320px] grid grid-cols-2 gap-4">
      <div className="space-y-2">
        <div className="mk-mono text-[10px] uppercase tracking-[0.2em] text-[var(--mk-accent)]">From template</div>
        <div className="text-sm font-semibold">HVAC annual service</div>
        <div className="space-y-1.5">
          {[
            ["Site visit + inspection", "£85.00"],
            ["Filter replacement ×2", "£49.00"],
            ["Compressor check", "£120.00"],
            ["Refrigerant top-up (if req.)", "£65.00"],
          ].map(([k, v]) => (
            <div key={k} className="text-xs flex justify-between border-b pb-1.5" style={{ borderColor: "var(--mk-border-subtle)" }}>
              <span>{k}</span>
              <span className="text-[var(--mk-text-dim)]">{v}</span>
            </div>
          ))}
        </div>
      </div>
      <div className="space-y-2 rounded-lg p-3 border" style={{ borderColor: "var(--mk-border-accent)", background: "rgba(214,239,55,0.04)" }}>
        <div className="mk-mono text-[10px] uppercase tracking-[0.2em] text-[var(--mk-accent)]">Send to client</div>
        <div className="text-sm">Chris @ Belmont Estates</div>
        <div className="rounded-md p-3 text-xs" style={{ background: "rgba(0,0,0,0.35)" }}>
          <div className="mb-2 font-semibold">📧 Subject: Quote Q-2041 — HVAC annual service</div>
          <div className="text-[var(--mk-text-dim)]">Approve or reject in one tap →</div>
          <div className="mt-2 inline-flex gap-2">
            <div className="mk-btn mk-btn-primary text-xs">Approve</div>
            <div className="mk-btn mk-btn-secondary text-xs">Reject</div>
          </div>
        </div>
        <div className="mk-mono text-[9px] text-[var(--mk-text-muted)] mt-2">Signed at approval · audit logged</div>
      </div>
    </div>
  </BrowserFrame>
);

export const QuotesComparisonMock = () => (
  <BrowserFrame url="app.fixlr.app/quotes/compare">
    <div className="p-5 min-h-[320px]">
      <div className="text-sm font-semibold mb-3">Compare quotes for Job J-2033</div>
      <div className="grid grid-cols-3 gap-3">
        {[
          { s: "North Heat Ltd", p: "£420", r: "★ 4.8", d: "2 days", w: true },
          { s: "Capital Plumbing", p: "£485", r: "★ 4.6", d: "1 day" },
          { s: "Greenline Gas", p: "£389", r: "★ 4.9", d: "4 days" },
        ].map((q) => (
          <div
            key={q.s}
            className="rounded-lg p-3 border relative"
            style={{
              borderColor: q.w ? "var(--mk-border-accent)" : "var(--mk-border-subtle)",
              background: q.w ? "rgba(214,239,55,0.06)" : "rgba(255,255,255,0.015)",
            }}
          >
            {q.w && (
              <div className="absolute -top-2 right-3">
                <Chip tone="accent">Pick winner</Chip>
              </div>
            )}
            <div className="text-sm font-semibold mb-2">{q.s}</div>
            <div className="text-2xl font-black mb-1" style={{ color: q.w ? "var(--mk-accent)" : "var(--mk-text)" }}>{q.p}</div>
            <div className="text-xs text-[var(--mk-text-dim)] space-y-1">
              <div>Rating: {q.r}</div>
              <div>Lead time: {q.d}</div>
              <div>Compliance: ✅ Current</div>
            </div>
          </div>
        ))}
      </div>
    </div>
  </BrowserFrame>
);

// SCHEDULE grid
export const ScheduleGridMock = () => (
  <BrowserFrame url="app.fixlr.app/schedule">
    <div className="p-5 min-h-[320px]">
      <div className="text-sm font-semibold mb-3">Schedule · This week</div>
      <div className="grid grid-cols-8 text-[10px] mk-mono uppercase tracking-[0.2em] text-[var(--mk-text-muted)] mb-2">
        <div />
        {["Mon", "Tue", "Wed", "Thu", "Fri", "Sat", "Sun"].map((d) => (
          <div key={d} className="text-center">{d}</div>
        ))}
      </div>
      {["Jay", "Nina", "Priya", "Ben"].map((p, r) => (
        <div key={p} className="grid grid-cols-8 gap-1 mb-1.5">
          <div className="text-xs font-semibold py-1">{p}</div>
          {Array.from({ length: 7 }).map((_, c) => {
            const has = [(r + c) % 3 === 0, (r + c) % 4 === 0, (r + c) % 5 === 0];
            return (
              <div key={c} className="rounded h-10 p-1 space-y-1 overflow-hidden" style={{ background: "rgba(255,255,255,0.02)", border: "1px solid var(--mk-border-subtle)" }}>
                {has[0] && <div style={{ height: 6, borderRadius: 2, background: "rgba(19,93,212,0.5)" }} />}
                {has[1] && <div style={{ height: 6, borderRadius: 2, background: "rgba(214,239,55,0.5)" }} />}
                {has[2] && <div style={{ height: 6, borderRadius: 2, background: "rgba(16,185,129,0.5)" }} />}
              </div>
            );
          })}
        </div>
      ))}
      <div className="flex gap-3 mt-3 text-[10px] text-[var(--mk-text-muted)]">
        <span>🔵 Scheduled</span><span>🔹 Confirmed</span><span>🟢 Complete</span>
      </div>
    </div>
  </BrowserFrame>
);

// IN-HOUSE portal mocks
export const InHousePWAMock = () => (
  <BrowserFrame url="app.fixlr.app/my-work">
    <div className="p-5 min-h-[320px] space-y-3">
      <div className="rounded-lg p-3 border flex items-start gap-3" style={{ borderColor: "var(--mk-border-accent)", background: "rgba(214,239,55,0.05)" }}>
        <div className="grid place-items-center rounded-lg" style={{ height: 40, width: 40, background: "var(--mk-primary)", color: "white", fontWeight: 900 }}>F</div>
        <div className="flex-1">
          <div className="text-sm font-semibold">Install Fixlr on this phone</div>
          <div className="text-xs text-[var(--mk-text-dim)]">Pin to home screen + get push alerts when jobs land.</div>
        </div>
        <div className="mk-btn mk-btn-primary text-xs">Install</div>
      </div>
      <div className="rounded-lg border p-3" style={{ borderColor: "var(--mk-border-subtle)" }}>
        <div className="flex items-center justify-between mb-2">
          <div className="text-sm font-semibold">Up next</div>
          <Chip tone="accent">⏱ 2h 25m on site</Chip>
        </div>
        {[
          { t: "Boiler service", a: "14 Mare St", tm: "14:30" },
          { t: "Leak investigation", a: "92 Bethnal Green", tm: "16:00" },
        ].map((j) => (
          <Row key={j.t}>
            <div>
              <div className="text-xs font-semibold">{j.t}</div>
              <div className="text-[10px] text-[var(--mk-text-dim)]">{j.a}</div>
            </div>
            <Chip tone="primary">{j.tm}</Chip>
          </Row>
        ))}
      </div>
    </div>
  </BrowserFrame>
);

export const SnapshotMock = () => (
  <BrowserFrame url="app.fixlr.app/my-work">
    <div className="p-5 min-h-[320px]">
      <div className="grid grid-cols-3 gap-3">
        {[
          { t: "Up Next Today", c: "primary", n: 3, rows: ["Boiler service — 14:30", "Leak inv. — 16:00", "PPM HVAC — 17:30"] },
          { t: "Done Today", c: "success", n: 2, rows: ["Door repair — 45m", "PAT testing — 62m"] },
          { t: "Need Time Logged", c: "warn", n: 1, rows: ["Fire door — missing log"] },
        ].map((col) => (
          <div key={col.t} className="rounded-lg border p-3 space-y-2" style={{ borderColor: "var(--mk-border-subtle)" }}>
            <div className="flex items-center justify-between">
              <div className="text-xs font-bold">{col.t}</div>
              <Chip tone={col.c}>{col.n}</Chip>
            </div>
            {col.rows.map((r) => (
              <div key={r} className="text-[10px] text-[var(--mk-text-dim)] rounded px-2 py-1.5" style={{ background: "rgba(255,255,255,0.02)" }}>
                {r}
              </div>
            ))}
          </div>
        ))}
      </div>
    </div>
  </BrowserFrame>
);

export const BriefingEmailMock = () => (
  <BrowserFrame url="mail.client.com">
    <div className="p-5 min-h-[320px] space-y-2">
      <div className="mk-mono text-[10px] uppercase tracking-[0.2em] text-[var(--mk-text-muted)]">FROM: briefings@fixli.io · 06:30 local</div>
      <div className="text-base font-bold">Good morning Jay — today's 3 jobs</div>
      <div className="rounded-lg border" style={{ borderColor: "var(--mk-border-subtle)" }}>
        {[
          ["Boiler service", "14:30 · 14 Mare St"],
          ["Leak investigation", "16:00 · 92 Bethnal Green"],
          ["PPM HVAC", "17:30 · Unit 4 Holloway"],
        ].map(([t, d], i, arr) => (
          <Row key={t} last={i === arr.length - 1}>
            <div>
              <div className="text-xs font-semibold">{t}</div>
              <div className="text-[10px] text-[var(--mk-text-dim)]">{d}</div>
            </div>
            <div className="flex gap-2">
              <div className="mk-btn mk-btn-secondary text-[10px]">Directions</div>
              <div className="mk-btn mk-btn-primary text-[10px]">Open →</div>
            </div>
          </Row>
        ))}
      </div>
    </div>
  </BrowserFrame>
);

// LIVE TEAM MAP
export const LiveTeamMapMock = () => (
  <BrowserFrame url="app.fixlr.app/live-team-map">
    <div className="min-h-[340px] relative" style={{
      background: `radial-gradient(circle at 30% 40%, rgba(19,93,212,0.18) 0%, transparent 50%),
                   radial-gradient(circle at 70% 60%, rgba(214,239,55,0.12) 0%, transparent 55%),
                   linear-gradient(180deg, #1e1f19, #141414)`,
      overflow: "hidden",
    }}>
      <div className="mk-grid-bg absolute inset-0 opacity-40" />
      {[
        { x: 18, y: 28, c: "#10b981", n: "Jay" },
        { x: 44, y: 52, c: "#d6ef37", n: "Nina" },
        { x: 72, y: 38, c: "#f59e0b", n: "Priya" },
        { x: 58, y: 72, c: "#10b981", n: "Ben" },
      ].map((p) => (
        <div key={p.n} className="absolute" style={{ left: `${p.x}%`, top: `${p.y}%`, transform: "translate(-50%, -50%)" }}>
          <div style={{ width: 16, height: 16, borderRadius: "50%", background: p.c, boxShadow: `0 0 0 4px ${p.c}33` }} />
          <div className="absolute left-1/2 -translate-x-1/2 mt-1 mk-mono text-[9px] font-bold text-white/80 whitespace-nowrap">{p.n}</div>
        </div>
      ))}
      <div className="absolute top-3 right-3 rounded-lg p-3 border backdrop-blur-xl" style={{ borderColor: "var(--mk-border-subtle)", background: "rgba(20,20,20,0.7)" }}>
        <div className="mk-mono text-[9px] uppercase tracking-[0.2em] text-[var(--mk-accent)] mb-2">On shift</div>
        {["🟢 Jay — on site", "🔵 Nina — en route", "🟡 Priya — break", "🟢 Ben — on site"].map((t) => (
          <div key={t} className="text-[10px] text-[var(--mk-text-dim)] mb-1">{t}</div>
        ))}
      </div>
    </div>
  </BrowserFrame>
);

export const PublicTrackerMock = () => (
  <BrowserFrame url="share.fixlr.app/t/7x9q2">
    <div className="min-h-[340px] p-4 space-y-3">
      <div className="mk-mono text-[10px] uppercase tracking-[0.2em] text-[var(--mk-accent)]">Live tracker · shared by Belmont</div>
      <div className="text-sm font-bold">Jay Patel is on the way</div>
      <div className="relative rounded-lg overflow-hidden" style={{ height: 170, background: `linear-gradient(180deg, #1e1f19, #141414)` }}>
        <div className="mk-grid-bg absolute inset-0 opacity-40" />
        <div className="absolute" style={{ left: "15%", top: "65%" }}><div style={{ width: 14, height: 14, borderRadius: "50%", background: "#10b981", boxShadow: "0 0 0 6px rgba(16,185,129,0.3)" }} /></div>
        <div className="absolute" style={{ left: "78%", top: "28%" }}><div style={{ width: 12, height: 12, borderRadius: "50%", background: "#d6ef37" }} /></div>
        <svg className="absolute inset-0 w-full h-full" preserveAspectRatio="none">
          <path d="M 15% 65% Q 50% 20%, 78% 28%" stroke="#d6ef37" strokeWidth="2" fill="none" strokeDasharray="4 4" />
        </svg>
      </div>
      <div className="grid grid-cols-3 text-xs">
        <div><div className="mk-mono text-[9px] text-[var(--mk-text-muted)] uppercase tracking-[0.2em]">ETA</div><div className="font-semibold">14:28</div></div>
        <div><div className="mk-mono text-[9px] text-[var(--mk-text-muted)] uppercase tracking-[0.2em]">Distance</div><div className="font-semibold">2.1 km</div></div>
        <div><div className="mk-mono text-[9px] text-[var(--mk-text-muted)] uppercase tracking-[0.2em]">Status</div><div className="font-semibold text-[color:var(--mk-accent)]">En route</div></div>
      </div>
    </div>
  </BrowserFrame>
);

// CLIENT PORTAL
export const ClientPortalDashboardMock = () => (
  <BrowserFrame url="belmont.fixlr.app/portal">
    <div className="p-5 min-h-[320px] space-y-3">
      <div className="flex justify-between items-center">
        <div>
          <div className="mk-mono text-[9px] uppercase tracking-[0.2em] text-[var(--mk-text-muted)]">Client portal</div>
          <div className="text-sm font-bold">Belmont Estates · 4 properties</div>
        </div>
        <div className="mk-btn mk-btn-primary text-xs">+ New request</div>
      </div>
      <div className="grid grid-cols-4 gap-2">
        {[["Open jobs", "6"], ["Quotes pending", "2"], ["Paid invoices", "18"], ["Avg resolve", "1.3d"]].map(([k, v]) => (
          <div key={k} className="rounded-lg p-3 border" style={{ borderColor: "var(--mk-border-subtle)" }}>
            <div className="mk-mono text-[9px] uppercase tracking-[0.2em] text-[var(--mk-text-muted)]">{k}</div>
            <div className="text-lg font-bold mt-1">{v}</div>
          </div>
        ))}
      </div>
      <div className="rounded-lg border overflow-hidden" style={{ borderColor: "var(--mk-border-subtle)" }}>
        {[
          ["Boiler service — 14 Mare St", "In progress"],
          ["Lift inspection — Camden Tower", "Scheduled"],
          ["Fire door repair — Unit 7", "Completed"],
        ].map(([j, s], i, a) => (
          <Row key={j} last={i === a.length - 1}>
            <div className="text-xs">{j}</div>
            <Chip tone={s === "Completed" ? "success" : s === "Scheduled" ? "accent" : "warn"}>{s}</Chip>
          </Row>
        ))}
      </div>
    </div>
  </BrowserFrame>
);

export const ClientPortalRequestMock = () => (
  <BrowserFrame url="belmont.fixlr.app/portal/request/new">
    <div className="p-5 min-h-[320px] space-y-3">
      <div className="text-sm font-bold">Raise a job request</div>
      <div className="rounded-lg p-3 border" style={{ borderColor: "var(--mk-border-subtle)" }}>
        <div className="mk-mono text-[9px] uppercase tracking-[0.2em] text-[var(--mk-text-muted)]">Property</div>
        <div className="text-sm font-semibold mt-1">14 Mare St, E8 3PL</div>
      </div>
      <div className="rounded-lg p-3 border" style={{ borderColor: "var(--mk-border-subtle)" }}>
        <div className="mk-mono text-[9px] uppercase tracking-[0.2em] text-[var(--mk-text-muted)]">What's the issue?</div>
        <div className="text-xs text-[var(--mk-text-dim)] mt-2 leading-relaxed">Kitchen radiator not heating. Rest of flat is fine. Tenant has been without hot water since Monday.</div>
      </div>
      <div className="grid grid-cols-3 gap-2">
        {["Emergency", "Urgent", "Routine"].map((l, i) => (
          <div key={l} className="rounded-lg p-2 text-center text-xs border" style={{
            background: i === 1 ? "rgba(245,158,11,0.08)" : "rgba(255,255,255,0.02)",
            borderColor: i === 1 ? "rgba(245,158,11,0.35)" : "var(--mk-border-subtle)",
            color: i === 1 ? "#f59e0b" : "var(--mk-text-dim)",
          }}>
            {l}
          </div>
        ))}
      </div>
      <div className="flex justify-end"><div className="mk-btn mk-btn-primary text-xs">Send →</div></div>
    </div>
  </BrowserFrame>
);

// SUPPLIER portal
export const SupplierPortalDashboardMock = () => (
  <BrowserFrame url="supplier.fixlr.app/northheat/dashboard">
    <div className="p-5 min-h-[320px] space-y-3">
      <div className="grid grid-cols-4 gap-2">
        {[["Open jobs", "4"], ["Awaiting quote", "2"], ["Paid this month", "£3.2k"], ["SLA hit", "96%"]].map(([k, v]) => (
          <div key={k} className="rounded-lg p-2 border" style={{ borderColor: "var(--mk-border-subtle)" }}>
            <div className="mk-mono text-[9px] uppercase tracking-[0.2em] text-[var(--mk-text-muted)]">{k}</div>
            <div className="text-base font-bold mt-1">{v}</div>
          </div>
        ))}
      </div>
      <div className="rounded-lg border overflow-hidden" style={{ borderColor: "var(--mk-border-subtle)" }}>
        <div className="px-3 py-2 mk-mono text-[10px] uppercase tracking-[0.2em] text-[var(--mk-text-muted)]" style={{ background: "rgba(255,255,255,0.02)" }}>My jobs</div>
        {[
          ["J-2041 — Boiler service", "Scheduled Tue 14:30", "accent"],
          ["J-2045 — HVAC callout", "Awaiting quote", "warn"],
          ["J-2038 — Gas safety cert", "Complete · Invoice sent", "success"],
        ].map(([j, s, t], i, a) => (
          <Row key={j} last={i === a.length - 1}>
            <div className="text-xs">{j}</div>
            <Chip tone={t}>{s}</Chip>
          </Row>
        ))}
      </div>
    </div>
  </BrowserFrame>
);

export const SupplierPortalMobileMock = () => (
  <BrowserFrame url="supplier.fixlr.app/m">
    <div className="p-5 min-h-[340px] flex justify-center">
      <div className="rounded-[28px] p-3 border" style={{ width: 220, borderColor: "var(--mk-border-accent)", background: "var(--mk-surface)" }}>
        <div className="text-center mk-mono text-[9px] uppercase tracking-[0.2em] text-[var(--mk-text-muted)] mb-2">09:42</div>
        <div className="rounded-xl p-3 mb-2" style={{ background: "rgba(214,239,55,0.08)", border: "1px solid var(--mk-border-accent)" }}>
          <div className="mk-mono text-[9px] uppercase tracking-[0.2em] text-[var(--mk-accent)]">CURRENT JOB</div>
          <div className="text-xs font-bold mt-1">Boiler service — 14 Mare St</div>
          <div className="text-[10px] text-[var(--mk-text-dim)]">Arrived: 14:32</div>
        </div>
        <div className="space-y-1.5">
          <div className="mk-btn mk-btn-primary text-[11px] w-full justify-center">Mark complete ✓</div>
          <div className="mk-btn mk-btn-secondary text-[11px] w-full justify-center">Log time</div>
          <div className="mk-btn mk-btn-secondary text-[11px] w-full justify-center">Upload photo</div>
        </div>
        <div className="mt-3 rounded-lg p-2 text-[10px] text-[var(--mk-text-dim)]" style={{ background: "rgba(255,255,255,0.02)" }}>
          Next: Leak investigation @ 16:00
        </div>
      </div>
    </div>
  </BrowserFrame>
);

// SUPPLIER NETWORK
export const SupplierBulkInviteMock = () => (
  <BrowserFrame url="app.fixlr.app/platform/bulk-supplier-invite">
    <div className="p-5 min-h-[320px] space-y-3">
      <div className="text-sm font-bold">Bulk supplier invite</div>
      <div className="rounded-lg p-4 border-dashed border text-center" style={{ borderColor: "var(--mk-border-accent)", background: "rgba(214,239,55,0.03)" }}>
        <div className="text-xs text-[var(--mk-text-dim)]">Drop suppliers.csv here or click to browse</div>
        <div className="mk-mono text-[10px] text-[var(--mk-text-muted)] mt-1">Preview · 487 rows loaded</div>
      </div>
      <div className="rounded-lg border overflow-hidden" style={{ borderColor: "var(--mk-border-subtle)" }}>
        <div className="grid grid-cols-4 px-3 py-2 text-[10px] mk-mono uppercase tracking-[0.2em] text-[var(--mk-text-muted)]" style={{ background: "rgba(255,255,255,0.02)" }}>
          <div>Name</div><div>Trade</div><div>Email</div><div>Status</div>
        </div>
        {[
          ["North Heat Ltd", "HVAC", "ops@northheat.co.uk", "✓ Ready"],
          ["Capital Plumbing", "Plumbing", "info@capitalplumb.com", "✓ Ready"],
          ["Greenline Gas", "Gas safe", "hello@greenline.io", "✓ Ready"],
          ["Sparks Electrical", "Electrical", "—", "⚠ Email missing"],
        ].map((r, i, a) => (
          <div key={r[0]} className="grid grid-cols-4 px-3 py-2 text-xs border-t" style={{ borderColor: "var(--mk-border-subtle)" }}>
            <div>{r[0]}</div><div className="text-[var(--mk-text-dim)]">{r[1]}</div><div className="text-[var(--mk-text-dim)]">{r[2]}</div><div className={r[3].startsWith("⚠") ? "text-amber-500" : "text-emerald-500"}>{r[3]}</div>
          </div>
        ))}
      </div>
      <div className="flex justify-end"><div className="mk-btn mk-btn-primary text-xs">Send 486 invites</div></div>
    </div>
  </BrowserFrame>
);

export const SupplierApplicationMock = () => (
  <BrowserFrame url="acme.fixlr.app/apply">
    <div className="p-5 min-h-[320px] grid grid-cols-2 gap-4">
      <div>
        <div className="text-sm font-bold">Join Acme Facilities' supplier network</div>
        <div className="text-xs text-[var(--mk-text-dim)] mt-1">Fill in your details, upload certs, get approved. Usually in &lt;24h.</div>
        <div className="space-y-2 mt-3">
          {[["Company name", "North Heat Ltd"], ["Trade", "Gas · HVAC"], ["Coverage area", "Greater London"], ["Public liability", "£5m — exp 12/2026"]].map(([k, v]) => (
            <div key={k} className="rounded-lg p-2 border" style={{ borderColor: "var(--mk-border-subtle)" }}>
              <div className="mk-mono text-[9px] uppercase tracking-[0.2em] text-[var(--mk-text-muted)]">{k}</div>
              <div className="text-xs mt-0.5">{v}</div>
            </div>
          ))}
        </div>
      </div>
      <div className="rounded-lg p-3 border space-y-2" style={{ borderColor: "var(--mk-border-subtle)" }}>
        <div className="mk-mono text-[10px] uppercase tracking-[0.2em] text-[var(--mk-accent)]">Uploads required</div>
        {["Public Liability — ✅ uploaded", "Gas Safe Reg — ✅ uploaded", "RAMS template — ✅ uploaded", "DBS — ⚠ missing"].map((l) => (
          <div key={l} className="text-xs text-[var(--mk-text-dim)]">{l}</div>
        ))}
        <div className="mk-btn mk-btn-primary text-xs w-full justify-center mt-2">Submit application</div>
      </div>
    </div>
  </BrowserFrame>
);

// SUPPLIER COMPLIANCE matrix
export const SupplierComplianceMock = () => (
  <BrowserFrame url="app.fixlr.app/supplier-compliance">
    <div className="p-5 min-h-[320px]">
      <div className="text-sm font-bold mb-3">Supplier compliance matrix</div>
      <div className="rounded-lg border overflow-hidden" style={{ borderColor: "var(--mk-border-subtle)" }}>
        <div className="grid grid-cols-6 px-3 py-2 text-[10px] mk-mono uppercase tracking-[0.2em] text-[var(--mk-text-muted)]" style={{ background: "rgba(255,255,255,0.02)" }}>
          <div className="col-span-2">Supplier</div><div>PL Ins.</div><div>RAMS</div><div>Gas Safe</div><div>DBS</div>
        </div>
        {[
          ["North Heat Ltd", "ok", "ok", "ok", "ok"],
          ["Capital Plumbing", "ok", "ok", "warn", "ok"],
          ["Greenline Gas", "ok", "ok", "ok", "miss"],
          ["Sparks Electrical", "warn", "ok", "—", "ok"],
        ].map((r, i) => (
          <div key={r[0]} className="grid grid-cols-6 px-3 py-2 text-xs border-t items-center" style={{ borderColor: "var(--mk-border-subtle)" }}>
            <div className="col-span-2 font-semibold">{r[0]}</div>
            {r.slice(1).map((s, j) => (
              <div key={j}>
                {s === "ok" && <Chip tone="success">OK</Chip>}
                {s === "warn" && <Chip tone="warn">30d</Chip>}
                {s === "miss" && <Chip tone="danger">Missing</Chip>}
                {s === "—" && <span className="text-[var(--mk-text-muted)]">—</span>}
              </div>
            ))}
          </div>
        ))}
      </div>
      <div className="mt-3 text-xs text-[var(--mk-text-dim)]">⏰ 12 suppliers have certs expiring in the next 30 days — auto-emails queued.</div>
    </div>
  </BrowserFrame>
);

// AI
export const AISupplierMatchMock = () => (
  <BrowserFrame url="app.fixlr.app/ai-assistant">
    <div className="p-5 min-h-[320px]">
      <div className="text-sm font-bold mb-2">AI supplier match — J-2041 Boiler service</div>
      <div className="space-y-2">
        {[
          { r: 1, s: "North Heat Ltd", score: 94, reasons: ["2km away", "Gas Safe current", "SLA 96%", "£420 avg"] },
          { r: 2, s: "Greenline Gas", score: 88, reasons: ["4km away", "★4.9 rating", "£389 lowest price"] },
          { r: 3, s: "Capital Plumbing", score: 76, reasons: ["6km away", "Full availability"] },
        ].map((m) => (
          <div key={m.s} className="rounded-lg p-3 border flex items-center gap-3" style={{
            borderColor: m.r === 1 ? "var(--mk-border-accent)" : "var(--mk-border-subtle)",
            background: m.r === 1 ? "rgba(214,239,55,0.04)" : "rgba(255,255,255,0.015)",
          }}>
            <div className="text-center" style={{ width: 44 }}>
              <div className="mk-mono text-[9px] text-[var(--mk-text-muted)] uppercase">Rank</div>
              <div className="text-lg font-bold">{m.r}</div>
            </div>
            <div className="flex-1">
              <div className="text-sm font-semibold">{m.s}</div>
              <div className="flex gap-1.5 flex-wrap mt-1">
                {m.reasons.map((r) => <Chip key={r} tone="muted">{r}</Chip>)}
              </div>
            </div>
            <div className="text-right">
              <div className="mk-mono text-[9px] text-[var(--mk-text-muted)] uppercase">Match</div>
              <div className="text-lg font-bold" style={{ color: m.r === 1 ? "var(--mk-accent)" : "var(--mk-text)" }}>{m.score}</div>
              {m.r === 1 && <div className="mk-btn mk-btn-primary text-[10px] mt-1">Assign</div>}
            </div>
          </div>
        ))}
      </div>
    </div>
  </BrowserFrame>
);

export const AIReassignMock = () => (
  <BrowserFrame url="app.fixlr.app/">
    <div className="p-5 min-h-[320px] space-y-3">
      <div className="rounded-lg p-3 border flex items-start gap-3" style={{ borderColor: "var(--mk-border-accent)", background: "rgba(214,239,55,0.05)" }}>
        <div className="mk-mono text-[10px] uppercase tracking-[0.2em] text-[var(--mk-accent)]">AI suggestion</div>
      </div>
      <div className="text-sm font-bold">2 operatives overloaded · 1 idle</div>
      <div className="grid grid-cols-3 gap-3">
        {[
          { n: "Jay", load: 92, c: "#ef4444", tag: "Overloaded" },
          { n: "Nina", load: 87, c: "#f59e0b", tag: "Near cap" },
          { n: "Ben", load: 24, c: "#10b981", tag: "Idle" },
        ].map((o) => (
          <div key={o.n} className="rounded-lg p-3 border" style={{ borderColor: "var(--mk-border-subtle)" }}>
            <div className="text-sm font-semibold mb-2">{o.n}</div>
            <Bar w={o.load} color={o.c} />
            <div className="flex justify-between mt-2 text-[10px]"><span className="text-[var(--mk-text-dim)]">{o.load}%</span><Chip tone={o.load > 85 ? "danger" : o.load > 70 ? "warn" : "success"}>{o.tag}</Chip></div>
          </div>
        ))}
      </div>
      <div className="flex items-center justify-between rounded-lg border p-3" style={{ borderColor: "var(--mk-border-subtle)" }}>
        <div className="text-xs">Move <b>J-2047</b> and <b>J-2049</b> from Jay → Ben?</div>
        <div className="flex gap-2"><div className="mk-btn mk-btn-secondary text-xs">Later</div><div className="mk-btn mk-btn-primary text-xs">Reassign</div></div>
      </div>
    </div>
  </BrowserFrame>
);

// INVOICING
export const InvoiceCreateMock = () => <JobsCostingsMock />;

// ASSETS
export const AssetsRegisterMock = () => (
  <BrowserFrame url="app.fixlr.app/assets">
    <div className="p-5 min-h-[320px] space-y-3">
      <div className="flex justify-between">
        <div className="text-sm font-bold">Asset register · 14 Mare St</div>
        <div className="mk-btn mk-btn-secondary text-xs">Print QR pack</div>
      </div>
      <div className="rounded-lg border overflow-hidden" style={{ borderColor: "var(--mk-border-subtle)" }}>
        <div className="grid grid-cols-5 px-3 py-2 text-[10px] mk-mono uppercase tracking-[0.2em] text-[var(--mk-text-muted)]" style={{ background: "rgba(255,255,255,0.02)" }}>
          <div>Asset</div><div>Ref</div><div>Warranty</div><div>Last service</div><div>Next due</div>
        </div>
        {[
          ["Vaillant Ecotec 835 boiler", "AST-0042", "✓ 2027", "Jan 2026", "Jan 2027"],
          ["Daikin HVAC unit", "AST-0071", "✓ 2029", "Nov 2025", "May 2026"],
          ["Fire door (kitchen)", "AST-0108", "—", "Sep 2025", "Mar 2026"],
          ["Emergency lighting", "AST-0112", "✓ 2026", "Feb 2026", "Feb 2027"],
        ].map((r) => (
          <div key={r[1]} className="grid grid-cols-5 px-3 py-2 text-xs border-t items-center" style={{ borderColor: "var(--mk-border-subtle)" }}>
            <div>{r[0]}</div>
            <div className="mk-mono text-[var(--mk-text-dim)]">{r[1]}</div>
            <div className="text-[var(--mk-text-dim)]">{r[2]}</div>
            <div className="text-[var(--mk-text-dim)]">{r[3]}</div>
            <div><Chip tone="accent">{r[4]}</Chip></div>
          </div>
        ))}
      </div>
    </div>
  </BrowserFrame>
);

// JOB SHEETS
export const JobSheetsBuilderMock = () => (
  <BrowserFrame url="app.fixlr.app/templates">
    <div className="p-5 min-h-[320px] grid grid-cols-5 gap-3">
      <div className="col-span-2 space-y-2">
        <div className="mk-mono text-[10px] uppercase tracking-[0.2em] text-[var(--mk-accent)]">Field palette</div>
        {["Text", "Number", "Checkbox", "Dropdown", "Signature", "Photo"].map((f) => (
          <div key={f} className="rounded-lg px-3 py-2 text-xs border" style={{ borderColor: "var(--mk-border-subtle)", background: "rgba(255,255,255,0.02)" }}>{f}</div>
        ))}
      </div>
      <div className="col-span-3 rounded-lg border p-3 space-y-2" style={{ borderColor: "var(--mk-border-accent)", background: "rgba(214,239,55,0.04)" }}>
        <div className="text-xs font-bold">Gas Safety Record — preview</div>
        {[
          ["Engineer signature", "SIGN"],
          ["Gas inlet pressure (mbar)", "NUMBER"],
          ["Flue flow test passed?", "CHECK"],
          ["Appliance photo", "PHOTO"],
          ["Notes", "TEXT"],
        ].map(([l, t]) => (
          <div key={l} className="flex justify-between items-center rounded-md px-3 py-2 text-xs" style={{ background: "rgba(20,20,20,0.4)", border: "1px solid var(--mk-border-subtle)" }}>
            <span>{l}</span><Chip tone="accent">{t}</Chip>
          </div>
        ))}
      </div>
    </div>
  </BrowserFrame>
);

// REPORTS dashboard
export const ReportsDashboardMock = () => (
  <BrowserFrame url="app.fixlr.app/reports">
    <div className="p-5 min-h-[320px] space-y-3">
      <div className="grid grid-cols-4 gap-3">
        {[["MRR", "£42.8k", "+12%"], ["Active jobs", "184", "+6%"], ["SLA hit rate", "94%", "+2%"], ["First-time fix", "86%", "+3%"]].map(([k, v, d]) => (
          <div key={k} className="rounded-lg p-3 border" style={{ borderColor: "var(--mk-border-subtle)" }}>
            <div className="mk-mono text-[9px] uppercase tracking-[0.2em] text-[var(--mk-text-muted)]">{k}</div>
            <div className="text-xl font-bold mt-1">{v}</div>
            <div className="text-[10px] text-emerald-400 mt-0.5">{d} vs last month</div>
          </div>
        ))}
      </div>
      <div className="rounded-lg border p-3" style={{ borderColor: "var(--mk-border-subtle)" }}>
        <div className="mk-mono text-[10px] uppercase tracking-[0.2em] text-[var(--mk-accent)] mb-2">Jobs by week</div>
        <div className="flex items-end gap-2 h-24">
          {[48, 56, 62, 51, 74, 68, 82, 77, 89, 92, 85, 96].map((h, i) => (
            <div key={i} className="flex-1 rounded-t" style={{ height: `${h}%`, background: "linear-gradient(180deg, var(--mk-accent), var(--mk-primary))", opacity: 0.8 }} />
          ))}
        </div>
      </div>
    </div>
  </BrowserFrame>
);

// WHITE LABEL
export const WhiteLabelMock = () => (
  <BrowserFrame url="app.fixlr.app/platform/branding">
    <div className="p-5 min-h-[320px] grid grid-cols-2 gap-4">
      <div className="space-y-2">
        <div className="text-sm font-bold">Brand & domain</div>
        {[
          ["Custom domain", "app.belmont.co"],
          ["TLS status", "✓ Auto-issued"],
          ["Primary color", "#0B7A5C"],
          ["Email sender", "noreply@belmont.co"],
        ].map(([k, v]) => (
          <div key={k} className="rounded-lg p-2 border flex justify-between text-xs" style={{ borderColor: "var(--mk-border-subtle)" }}>
            <span className="text-[var(--mk-text-dim)]">{k}</span><span className="font-semibold">{v}</span>
          </div>
        ))}
      </div>
      <div className="rounded-lg p-4 border flex flex-col items-center justify-center" style={{ borderColor: "var(--mk-border-accent)", background: "linear-gradient(180deg, rgba(11,122,92,0.1), transparent)" }}>
        <div className="mk-mono text-[9px] uppercase tracking-[0.2em] text-[var(--mk-text-muted)] mb-2">Tenant preview</div>
        <div className="h-10 w-10 rounded-lg grid place-items-center font-black text-white mb-2" style={{ background: "#0B7A5C" }}>B</div>
        <div className="text-sm font-bold">Belmont Estates</div>
        <div className="text-xs text-[var(--mk-text-dim)]">app.belmont.co</div>
      </div>
    </div>
  </BrowserFrame>
);

// INTEGRATIONS / WEBHOOKS
export const WebhooksMock = () => (
  <BrowserFrame url="app.fixlr.app/settings/webhooks">
    <div className="p-5 min-h-[320px] space-y-3">
      <div className="flex gap-2">
        {[["Generic", false], ["Slack", true], ["Teams", false]].map(([l, sel], i) => (
          <div key={l} className="rounded-lg px-3 py-1.5 text-xs border" style={{
            borderColor: sel ? "var(--mk-border-accent)" : "var(--mk-border-subtle)",
            background: sel ? "rgba(214,239,55,0.08)" : "transparent",
            color: sel ? "var(--mk-accent)" : "var(--mk-text-dim)",
          }}>{l}</div>
        ))}
      </div>
      <div className="rounded-lg border p-3 space-y-2" style={{ borderColor: "var(--mk-border-subtle)" }}>
        <div className="mk-mono text-[10px] uppercase tracking-[0.2em] text-[var(--mk-text-muted)]">Slack webhook</div>
        <div className="mk-mono text-xs bg-black/30 rounded px-2 py-1.5 border" style={{ borderColor: "var(--mk-border-subtle)" }}>https://hooks.slack.com/services/T03…</div>
        <div className="grid grid-cols-3 gap-1.5 mt-2">
          {["tenant.morning_brief ✓", "job.created", "job.completed ✓", "emergency.new ✓", "invoice.paid", "supplier.approved"].map((e) => (
            <div key={e} className="text-[10px] rounded px-2 py-1 border"
              style={{
                borderColor: e.endsWith("✓") ? "var(--mk-border-accent)" : "var(--mk-border-subtle)",
                color: e.endsWith("✓") ? "var(--mk-accent)" : "var(--mk-text-muted)",
              }}>{e}</div>
          ))}
        </div>
      </div>
    </div>
  </BrowserFrame>
);

// Generic fallback
export const GenericMock = ({ slug }) => (
  <BrowserFrame url={`app.fixlr.app/${slug || "feature"}`}>
    <div className="p-6 min-h-[300px] flex items-center justify-center">
      <div className="text-center">
        <div className="mk-mono text-[10px] uppercase tracking-[0.3em] text-[var(--mk-accent)] mb-3">Preview</div>
        <div className="text-xl font-bold">{slug || "Feature"}</div>
        <div className="text-xs text-[var(--mk-text-dim)] mt-2">Interactive UI · configurable per tenant</div>
      </div>
    </div>
  </BrowserFrame>
);

// Lookup by slug used by the feature page template
export const MOCK_REGISTRY = {
  "jobs-detail": JobsDetailMock,
  "jobs-assign": JobsAssignMock,
  "jobs-costings": JobsCostingsMock,
  "emergency-dashboard": EmergencyDashboardMock,
  "emergency-escalation": EmergencyEscalationMock,
  "quotes-builder": QuotesBuilderMock,
  "quotes-comparison": QuotesComparisonMock,
  "schedule-grid": ScheduleGridMock,
  "in-house-pwa": InHousePWAMock,
  "in-house-snapshot": SnapshotMock,
  "in-house-briefing-email": BriefingEmailMock,
  "live-team-map": LiveTeamMapMock,
  "nav-public-tracker": PublicTrackerMock,
  "client-portal-dashboard": ClientPortalDashboardMock,
  "client-portal-request": ClientPortalRequestMock,
  "supplier-portal-dashboard": SupplierPortalDashboardMock,
  "supplier-portal-mobile": SupplierPortalMobileMock,
  "supplier-bulk-invite": SupplierBulkInviteMock,
  "supplier-application": SupplierApplicationMock,
  "supplier-compliance-matrix": SupplierComplianceMock,
  "ai-supplier-match": AISupplierMatchMock,
  "ai-reassign-suggest": AIReassignMock,
  "invoices-create": InvoiceCreateMock,
  "assets-register": AssetsRegisterMock,
  "job-sheets-builder": JobSheetsBuilderMock,
  "reports-dashboard": ReportsDashboardMock,
  "white-label-branding": WhiteLabelMock,
  "integrations-webhooks": WebhooksMock,
};

// Slugs that have REAL product screenshots captured to
// /public/marketing-shots/<slug>.png by
// /app/backend/scripts/capture_marketing_shots.py.
// Populated by the capture script's post-run report. If empty, the CSS
// mocks below render. Can be extended at any time — BrowserFrame
// gracefully falls back to CSS on image load errors.
const REAL_SHOTS = new Set([
  // Captured 2026-05-01 via /app/backend/scripts/capture_marketing_shots.py
  "jobs-list",
  "quotes-builder",
  "invoices-create",
  "assets-register",
  "schedule-grid",
  "reports-dashboard",
  "supplier-compliance-matrix",
  "job-sheets-builder",
  "integrations-webhooks",
  "live-team-map",
  "ai-supplier-match",
  "in-house-snapshot",
  // Captured 2026-06 — Compliance Hub + Route Planner feature tour
  "compliance-hub",
  "route-planner",
  // Captured 2026-09 — Contracts, Client Portal, Staff Monitoring, WhatsApp
  "contracts",
  "staff-monitoring",
  "whatsapp-inbox",
  "client-portal-dashboard",
  "client-portal-requests",
  "client-portal-invoices",
]);

// Map a mock-slug to an alternative captured-image slug (when the visible
// feature page doesn't have a direct 1:1 captured screenshot).
const REAL_SHOT_ALIASES = {
  "jobs-detail": "jobs-list",
  "jobs-assign": "jobs-list",
  "jobs-costings": "invoices-create",
  "emergency-dashboard": "jobs-list",
  "quotes-comparison": "quotes-builder",
  "ai-reassign-suggest": "ai-supplier-match",
  "client-portal-request": "client-portal-requests",
};

function realShotPath(slug) {
  const target = REAL_SHOT_ALIASES[slug] || slug;
  if (!REAL_SHOTS.has(target)) return null;
  return `/marketing-shots/${target}.png`;
}

export function getMock(slug) {
  const Comp = MOCK_REGISTRY[slug];
  const imageSrc = realShotPath(slug);
  if (!Comp && !imageSrc) return () => <GenericMock slug={slug} />;
  if (!Comp) {
    // Real shot exists but no CSS mock was authored — frame the shot with a
    // generic fallback on load error.
    return function RealShotOnly() {
      return (
        <BrowserFrame imageSrc={imageSrc} url="app.fixlr.app">
          <GenericMock slug={slug} />
        </BrowserFrame>
      );
    };
  }
  if (!imageSrc) return Comp;
  // Real shot exists — render it inside a BrowserFrame. On <img onerror> the
  // frame renders its children which, here, is the original CSS mock as a
  // clean fallback (BrowserFrame inside BrowserFrame is rare + visually OK).
  return function MockWithRealShot() {
    return (
      <BrowserFrame imageSrc={imageSrc} url="app.fixlr.app">
        <Comp />
      </BrowserFrame>
    );
  };
}

